import React, { FC } from 'react';

import { SxProps } from '@mui/system';

import {
  IAdminRequestedDetailsData,
  ICalendarEventType,
  IPharmacistDetails,
  IWorkshift,
  ProfessionalReplacementTypes,
  TypeOfSummary,
} from '@pharmaplan/common';
import { ReactComponent as CalendarIcon } from '@pharmaplan/common/assets/icons/calendarIconNew.svg';
import { ReactComponent as FeesIcon } from '@pharmaplan/common/assets/icons/feesIcon.svg';

import usePharmacistSummary from '../../../hooks/PharmacistSummary/usePharmacistSummary';
import useSelfService from '../../../hooks/useSelfService';
import ThemeConstants from '../../../theme/ThemeConstants';

import PharmacistSummaryContent from './PharmacistSummaryContent/PharmacistSummaryContent';
import DisplayRecurrence from './DisplayRecurrence/DisplayRecurrence';
import ServiceCard from '../ServiceCard/ServiceCard';
import { title } from './helpers';
import classes from './styles';
import { ReplacementLabels } from '../../../helpers/Constants';

interface IPharmacistSummary {
  event: Partial<IWorkshift> &
    Partial<IPharmacistDetails> &
    Partial<IAdminRequestedDetailsData>;
  DateTime?: React.ReactNode | null;
  summaryType?: TypeOfSummary;
  isCounterOffer?: boolean;
  customContainerStyle?: SxProps;
  type: ICalendarEventType;
  customTitle?: string;
  isAutoSyncRecord?: boolean;
  isLongTerm?:boolean;
}

const { dateContainer, dateTimeContainer, workshiftContainer } = classes;

const { grey850 } = ThemeConstants.common;

const PharmacistSummary: FC<IPharmacistSummary> = ({
  event,
  DateTime,
  summaryType,
  isCounterOffer,
  type,
  isAutoSyncRecord,
  customContainerStyle,
  customTitle,
  isLongTerm,
}) => {
  const { isSelfService } = useSelfService();

  const {
    counterEndDate = '',
    counterStartDate = '',
    startDate = '',
    endDate = '',
    emergencyFees,
    notes,
    replacementType = ProfessionalReplacementTypes.pharmacist,
    otherAllowance,
  } = event ?? {};

  const {
    dateLabel,
    counterDateLabel,
    counterLabel,
    mealIcon,
    mealLabel,
    accommodationIcon,
    accomodationLabel,
    travelIcon,
    travelLabel,
    isPharmacistSummary,
    isWorkshiftDetails,
    hourlyRate,
    otherAllowanceLabel,
    emergencyFeesLabel,
    recurrenceRepeat,
    recurrenceStyle,
  } = usePharmacistSummary({
    event,
    type,
    summaryType,
    DateTime,
    isAutoSyncRecord,
  });

  const config = [
    {
      key: '0',
      component: <CalendarIcon fill={grey850} />,
      data: !!startDate,
      label: dateLabel,
      counterLabel,
      style: dateContainer,
    },
    DateTime
      ? {
        key: '1',
        component: DateTime,
        data: true,
        style: dateTimeContainer,
      }
      : {
        key: '11',
      },
    {
      key: '2',
      data: isPharmacistSummary,
      component: <DisplayRecurrence recurrenceRepeat={recurrenceRepeat} />,
      style: recurrenceStyle,
    },
    {
      key: '6',
      label: mealLabel,
      component: mealIcon,
      data: isSelfService,
      style: workshiftContainer,
    },
    {
      key: '4',
      label: travelLabel,
      component: travelIcon,
      data: isSelfService,
      style: workshiftContainer,
    },
    {
      key: '5',
      label: accomodationLabel,
      component: accommodationIcon,
      data: isSelfService,
      style: workshiftContainer,
    },
    {
      key: '3',
      label: emergencyFeesLabel,
      data: isSelfService && emergencyFees,
      component: <FeesIcon fill={grey850} />,
      style: workshiftContainer,
    },
    {
      key: '7',
      label: otherAllowanceLabel,
      data: isSelfService && otherAllowance,
      component: <FeesIcon fill={grey850} />,
      style: workshiftContainer,
    },
  ];

  const showDateStrike = isCounterOffer
    && (counterStartDate !== startDate || counterEndDate !== endDate);

  const counterOfferConfig = isCounterOffer && isWorkshiftDetails
    ? [
      {
        key: '0',
        component: <CalendarIcon fill={grey850} />,
        data: startDate,
        label: dateLabel,
        counterData: showDateStrike ? counterStartDate : '',
        counterLabel: showDateStrike ? counterDateLabel : '',
        style: dateContainer,
      },
    ]
    : [];

  const replacement = ReplacementLabels[replacementType];
  let cardTitle = title(summaryType);

  if (isSelfService && replacementType) {
    cardTitle = replacement;
  }

  const summaryContent = isCounterOffer ? counterOfferConfig : config;
  const longTermContent = isLongTerm ? config.slice(0, 2) : summaryContent;
  const displayNote = isPharmacistSummary ? notes : null;

  return (
    <ServiceCard
      title={customTitle ?? cardTitle}
      customContainerStyle={customContainerStyle}
      isCounterOffer={isCounterOffer}
      hourlyRate={hourlyRate}
      content={(
        <PharmacistSummaryContent
          summaryContent={longTermContent}
          notes={displayNote}
        />
      )}
    />
  );
};

export default PharmacistSummary;
